import {
  Image,
  Modal,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Platform,
} from "react-native";
import React, { useEffect, useState } from "react";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../../utility/AppColors";
import { AppFontStyle } from "../../../../../styles/AppFontStyle";
import { AppImages } from "../../../../../utility/AppImages";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { FlatList } from "react-native-gesture-handler";
import { toast } from "react-toastify";
import * as Clipboard from "expo-clipboard";

/**
 * Decoupled InviteSocialMediaModal for SoloStreaming.
 *
 * Refactored to use MOCK DATA and PROPS only.
 * Removed dependencies on:
 * - useLiveStream
 * - getReferralSession
 * - getLoginData
 * - storeUserActivity
 */

interface InviteSocialMediaModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  streamId: string;
  eventType?: string; // Default to LiveStreaming if not passed
  heading?: string;
  onClose?: () => void;
  backTapped?: () => void;
  // Optional pre-calculated URL to skip standard logic
  precalculatedUrl?: string;
}

export default function InviteSocialMediaModal({
  showModal = false,
  setShowModal,
  streamId,
  eventType = "LiveStreaming",
  heading = "Share event",
  onClose,
  backTapped,
  precalculatedUrl,
}: InviteSocialMediaModalProps) {
  const [showCreateLoader, setCreateShowLoader] = useState(false);
  const [isLinkSharedOrCopied, setIsLinkSharedOrCopied] = useState(false);
  // Mock shareable URL state
  const [shareableUrl, setShareableUrl] = useState<string | null>(
    precalculatedUrl || null
  );

  useEffect(() => {
    if (showModal) {
      // Mock loading effect
      setCreateShowLoader(true);
      setTimeout(() => {
        const mockUrl =
          precalculatedUrl ||
          `https://kallout.com/events/live-stream?eventId=${streamId}&ref=mockRef123`;
        setShareableUrl(mockUrl);
        setCreateShowLoader(false);
      }, 500);
    } else {
      setIsLinkSharedOrCopied(false);
    }
  }, [showModal, streamId, precalculatedUrl]);

  const getShareUrl = () => {
    return shareableUrl || "";
  };

  const shareOnPlatform = (platform: string) => {
    const url = getShareUrl();
    let shareUrl = "";

    switch (platform) {
      case "Whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
        break;
      case "Instagram":
        shareUrl = `instagram://sharesheet?text=${encodeURIComponent(url)}`;
        break;
      case "Twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          url
        )}`;
        break;
      case "Facebook":
        shareUrl = `fb-messenger://share?link=${encodeURIComponent(url)}`;
        break;
      default:
        break;
    }
    if (shareUrl) {
      if (Platform.OS === "web") {
        window.open(shareUrl, "_blank");
      }
      setIsLinkSharedOrCopied(true);
      // Removed storeUserActivity
    }
  };

  const copyToClipboard = async () => {
    try {
      const linkToCopy = getShareUrl();
      await Clipboard.setStringAsync(linkToCopy);
      toast.success("Link copied to clipboard");
      setIsLinkSharedOrCopied(true);
      // Removed storeUserActivity
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  const socialMediaOptions = [
    { id: 1, name: "Instagram", image: AppImages.icSocialInsta },
    { id: 2, name: "Whatsapp", image: AppImages.icSocialWhatsapp },
    { id: 3, name: "Twitter", image: AppImages.icSocialTwitter },
    { id: 4, name: "Facebook", image: AppImages.icSocialFacebook },
  ];

  const styles = useStyles();

  const onCloseClick = () => {
    setShowModal(false);
    if (onClose) onClose();
    if (!isLinkSharedOrCopied && backTapped) {
      backTapped();
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <LinearGradient
          colors={["#111827", "#000000"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.mainView}
        >
          {showCreateLoader ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator
                size="large"
                color={AppColors.appViolentColor_6D08C3}
                style={styles.loader}
              />
              <Text style={styles.loadingText}>Preparing Invite...</Text>
            </View>
          ) : (
            <>
              <View style={styles.header}>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_18,
                    { fontSize: 22, color: AppColors.white },
                  ]}
                >
                  {heading || "Challenge on social media"}
                </Text>
                <TouchableOpacity onPress={onCloseClick}>
                  <Image
                    source={AppImages.icCloseWhite}
                    style={styles.closeIcon}
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.divider} />

              {/* Share via section */}
              <View style={{ paddingTop: 24 }}>
                <Text style={styles.subtitleText}>
                  Challenge your Opponent via
                </Text>
                <FlatList
                  data={socialMediaOptions}
                  numColumns={4}
                  style={{ marginTop: 24 }}
                  scrollEnabled={false}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.socialIconContainer}
                      onPress={() => shareOnPlatform(item.name)}
                    >
                      <Image source={item.image} style={styles.socialIcon} />
                      <Text style={styles.socialIconText}>{item.name}</Text>
                    </TouchableOpacity>
                  )}
                  keyExtractor={(item) => item.id.toString()}
                  columnWrapperStyle={{
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                />
              </View>

              {/* Copy link section */}
              <View style={{ paddingTop: 24 }}>
                <Text style={styles.subtitleText}>Copy link</Text>
                <View style={styles.copyLinkContainer}>
                  <TouchableOpacity onPress={copyToClipboard}>
                    <Image
                      source={AppImages.icLinkSocial}
                      style={styles.linkIcon}
                    />
                  </TouchableOpacity>
                  <Text
                    numberOfLines={1}
                    style={{
                      color: AppColors.white,
                      paddingLeft: 12,
                      flexShrink: 1,
                      overflow: "hidden",
                      fontSize: 13,
                    }}
                  >
                    {getShareUrl()}
                  </Text>
                </View>
              </View>

              {/* Footer buttons */}
              <View style={styles.footerButtons}>
                <TouchableOpacity
                  style={styles.backButton}
                  onPress={onCloseClick}
                >
                  <Text style={styles.backButtonText}>Back</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={copyToClipboard}
                >
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </LinearGradient>
      </View>
    </Modal>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      height: "auto",
      width: "34%",
      padding: 24,
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 12,
    },
    closeIcon: {
      height: 28,
      width: 28,
      resizeMode: "contain",
    },
    divider: {
      height: 1,
      backgroundColor: "rgba(55, 65, 81, 0.5)",
    },
    subtitleText: {
      color: AppColors.white,
      fontSize: 16,
      fontWeight: "400",
      // fontFamily: "Anybody-Regular", // Assuming font family
    },
    shareText: {
      color: AppColors.white,
      fontSize: 14,
      fontWeight: "500",
      opacity: 0.8,
    },
    socialIconContainer: {
      alignItems: "center",
      marginVertical: 8,
      flex: 1,
    },
    socialIcon: {
      width: 52,
      height: 52,
      resizeMode: "contain",
      borderRadius: 12,
    },
    socialIconText: {
      color: AppColors.white,
      textAlign: "center",
      fontSize: 11,
      marginTop: 6,
      opacity: 0.8,
    },
    copyLinkContainer: {
      flexDirection: "row",
      backgroundColor: "rgba(31, 41, 55, 0.8)",
      marginTop: 12,
      marginBottom: 16,
      borderRadius: 8,
      alignItems: "center",
      padding: 14,
    },
    linkIcon: {
      height: 18,
      width: 18,
      resizeMode: "contain",
      tintColor: AppColors.white,
    },
    footerButtons: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingTop: 24,
      gap: 16,
    },
    backButton: {
      flex: 1,
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: 16,
    },
    backButtonText: {
      color: AppColors.white,
      fontSize: 15,
      fontWeight: "600",
    },
    copyButton: {
      flex: 1,
      backgroundColor: "rgba(239, 68, 68, 1)",
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: 16,
    },
    copyButtonText: {
      color: AppColors.white,
      fontSize: 14,
      fontWeight: "600",
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: 40,
      flexDirection: "column",
    },
    loader: {
      marginBottom: 16,
    },
    loadingText: {
      ...AppFontStyle.BOLD_ANYBODY_18,
      color: AppColors.white,
      fontSize: 20,
      textAlign: "center",
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {
      container: {
        justifyContent: "flex-end",
      },
      mainView: {
        width: "100%",
        padding: 15,
        paddingHorizontal: 12,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderWidth: 1,
        borderBottomWidth: 0,
        borderColor: "#DC2626",
        shadowColor: "rgba(232, 232, 232, 0.15)",
        shadowOffset: { width: 0, height: -3 },
        shadowOpacity: 1,
        shadowRadius: 14,
      },
      socialIcon: {
        width: 44,
        height: 44,
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        justifyContent: "flex-end",
      },
      mainView: {
        width: "100%",
        padding: 15,
        paddingHorizontal: 12,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderWidth: 1,
        borderBottomWidth: 0,
        borderColor: "#DC2626",
        shadowColor: "rgba(232, 232, 232, 0.15)",
        shadowOffset: { width: 0, height: -3 },
        shadowOpacity: 1,
        shadowRadius: 14,
      },
      socialIcon: {
        width: 40,
        height: 40,
      },
    },
  }
);
